import React, {useState, useEffect, useCallback} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    Chip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Card,
    CardContent,
    CardActions,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import {toast} from 'react-toastify';
import api, {configApi} from 'utils/api';

const styles = {
    toolbar: {
        display: 'flex',
        gap: 2,
        mb: 2,
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    searchField: {
        minWidth: 200,
        flexGrow: {xs: 1, md: 0},
    },
    filterField: {
        minWidth: 150,
    },
    table: {
        minWidth: 650,
    },
    emptyState: {
        textAlign: 'center',
        py: 6,
        color: 'text.secondary',
    },
    mobileCard: {
        mb: 2,
    },
    mobileCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 1,
    },
    mobileCardInfo: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        mb: 1,
    },
};

const productLabels = {
    'into-sign': 'Into Sign',
    'into-view': 'Into View',
};

export default function ConfigList({schemas, onEdit}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [presets, setPresets] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingPreset, setDeletingPreset] = useState(null);

    // 암호화 옵션 다이얼로그 상태
    const [encryptDialogOpen, setEncryptDialogOpen] = useState(false);
    const [encryptDialogPreset, setEncryptDialogPreset] = useState(null);
    const [encryptDialogAction, setEncryptDialogAction] = useState(null); // 'download' | 'copy'
    const [encryptDialogFields, setEncryptDialogFields] = useState({}); // { key: boolean }
    const [encryptDialogFieldList, setEncryptDialogFieldList] = useState([]); // [{ key, label }]
    const [encrypting, setEncrypting] = useState(false);

    const loadPresets = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: rowsPerPage,
                offset: page * rowsPerPage,
            });
            if (search) params.append('search', search);
            if (productFilter) params.append('product', productFilter);

            const res = await api.get(`/api/config/presets?${params.toString()}`);
            setPresets(res.data.presets);
            setTotal(res.data.total);
        } catch (err) {
            toast.error('설정 목록을 불러오는데 실패했습니다.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, productFilter]);

    useEffect(() => {
        loadPresets();
    }, [loadPresets]);

    // 검색 디바운스
    const [searchInput, setSearchInput] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(0);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const handleDelete = async () => {
        if (!deletingPreset) return;

        try {
            await api.delete(`/api/config/presets/${deletingPreset.id}`);
            toast.success('설정이 삭제되었습니다.');
            setDeleteDialogOpen(false);
            setDeletingPreset(null);
            loadPresets();
        } catch (err) {
            toast.error('삭제에 실패했습니다.');
            console.error(err);
        }
    };

    // 프리셋에서 암호화 가능한 필드 목록 추출 (boolean/select 제외, 값이 있는 필드)
    const getEncryptableFields = (preset) => {
        const schema = schemas[preset.product];
        if (!schema) return [];
        const configData = preset.configData || {};
        const fields = [];
        schema.categories.forEach(cat => {
            cat.properties.forEach(prop => {
                if (prop.type === 'boolean' || prop.type === 'select') return;
                const value = configData[prop.key];
                if (value === undefined || value === null) return;
                if (value === prop.default) return;
                if (value === '' && prop.default === '') return;
                fields.push({key: prop.key, label: prop.label});
            });
        });
        return fields;
    };

    const openEncryptDialog = (preset, action) => {
        const fields = getEncryptableFields(preset);
        setEncryptDialogPreset(preset);
        setEncryptDialogAction(action);
        setEncryptDialogFieldList(fields);
        setEncryptDialogFields({});
        setEncryptDialogOpen(true);
    };

    const handleDownload = (preset) => {
        const fields = getEncryptableFields(preset);
        if (fields.length > 0) {
            openEncryptDialog(preset, 'download');
        } else {
            executeDownload(preset);
        }
    };

    const handleCopy = (preset) => {
        const fields = getEncryptableFields(preset);
        if (fields.length > 0) {
            openEncryptDialog(preset, 'copy');
        } else {
            executeCopy(preset);
        }
    };

    const executeDownload = (preset, encryptedValues = {}) => {
        const content = generatePropertiesContent(preset, schemas[preset.product], encryptedValues);
        const blob = new Blob([content], {type: 'text/plain;charset=utf-8'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `config-override_${preset.client}_${preset.server_name}.properties`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('파일이 다운로드되었습니다.');
    };

    const executeCopy = (preset, encryptedValues = {}) => {
        const content = generatePropertiesContent(preset, schemas[preset.product], encryptedValues);
        navigator.clipboard.writeText(content);
        toast.success('클립보드에 복사되었습니다.');
    };

    const handleEncryptDialogConfirm = async () => {
        const preset = encryptDialogPreset;
        const action = encryptDialogAction;
        if (!preset) return;

        const configData = preset.configData || {};
        const checkedEntries = Object.entries(encryptDialogFields)
            .filter(([, checked]) => checked)
            .map(([key]) => ({key, value: String(configData[key])}))
            .filter(e => e.value && e.value.trim());

        let encryptedValues = {};
        if (checkedEntries.length > 0) {
            setEncrypting(true);
            try {
                const res = await configApi.encryptValues(checkedEntries);
                encryptedValues = res.data.encrypted;
            } catch (err) {
                toast.error('암호화 처리 중 오류가 발생했습니다.');
                console.error(err);
                setEncrypting(false);
                return;
            }
            setEncrypting(false);
        }

        setEncryptDialogOpen(false);
        if (action === 'download') {
            executeDownload(preset, encryptedValues);
        } else {
            executeCopy(preset, encryptedValues);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // 모바일 카드 렌더링
    const renderMobileCard = (preset) => (
        <Card key={preset.id} sx={styles.mobileCard} variant="outlined">
            <CardContent sx={{pb: 1}}>
                <Box sx={styles.mobileCardHeader}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {preset.client}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {preset.server_name}
                        </Typography>
                    </Box>
                    <Chip
                        label={productLabels[preset.product] || preset.product}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </Box>
                {preset.description && (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                        {preset.description}
                    </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                    수정일: {formatDate(preset.updated_at)}
                </Typography>
            </CardContent>
            <CardActions sx={{justifyContent: 'flex-end', pt: 0}}>
                <IconButton size="small" onClick={() => handleCopy(preset)}>
                    <ContentCopyIcon fontSize="small"/>
                </IconButton>
                <IconButton size="small" onClick={() => handleDownload(preset)}>
                    <DownloadIcon fontSize="small"/>
                </IconButton>
                <IconButton size="small" onClick={() => onEdit(preset)}>
                    <EditIcon fontSize="small"/>
                </IconButton>
                <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                        setDeletingPreset(preset);
                        setDeleteDialogOpen(true);
                    }}
                >
                    <DeleteIcon fontSize="small"/>
                </IconButton>
            </CardActions>
        </Card>
    );

    return (
        <Box>
            {/* 툴바 */}
            <Box sx={styles.toolbar}>
                <TextField
                    size="small"
                    placeholder="검색 (고객사, 서버명, 설명)"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={styles.searchField}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action"/>
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl size="small" sx={styles.filterField}>
                    <InputLabel>제품</InputLabel>
                    <Select
                        value={productFilter}
                        onChange={(e) => {
                            setProductFilter(e.target.value);
                            setPage(0);
                        }}
                        label="제품"
                    >
                        <MenuItem value="">전체</MenuItem>
                        {Object.keys(schemas).map((key) => (
                            <MenuItem key={key} value={key}>
                                {productLabels[key] || key}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{flexGrow: 1}}/>
                <Tooltip title="새로고침">
                    <IconButton onClick={loadPresets} disabled={loading}>
                        <RefreshIcon/>
                    </IconButton>
                </Tooltip>
            </Box>

            {/* 테이블 (데스크톱) / 카드 (모바일) */}
            {isMobile ? (
                <Box>
                    {presets.length === 0 ? (
                        <Box sx={styles.emptyState}>
                            <Typography>저장된 설정이 없습니다.</Typography>
                            <Typography variant="body2" sx={{mt: 1}}>
                                "새 설정 생성" 탭에서 설정을 추가하세요.
                            </Typography>
                        </Box>
                    ) : (
                        presets.map(renderMobileCard)
                    )}
                </Box>
            ) : (
                <TableContainer>
                    <Table sx={styles.table} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>제품</TableCell>
                                <TableCell>고객사</TableCell>
                                <TableCell>서버명</TableCell>
                                <TableCell>설명</TableCell>
                                <TableCell>수정일</TableCell>
                                <TableCell align="right">작업</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {presets.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={styles.emptyState}>
                                        <Typography>저장된 설정이 없습니다.</Typography>
                                        <Typography variant="body2" sx={{mt: 1}}>
                                            "새 설정 생성" 탭에서 설정을 추가하세요.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                presets.map((preset) => (
                                    <TableRow key={preset.id} hover>
                                        <TableCell>
                                            <Chip
                                                label={productLabels[preset.product] || preset.product}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{preset.client}</TableCell>
                                        <TableCell>{preset.server_name}</TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {preset.description || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{formatDate(preset.updated_at)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="복사">
                                                <IconButton size="small" onClick={() => handleCopy(preset)}>
                                                    <ContentCopyIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="다운로드">
                                                <IconButton size="small" onClick={() => handleDownload(preset)}>
                                                    <DownloadIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="수정">
                                                <IconButton size="small" onClick={() => onEdit(preset)}>
                                                    <EditIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="삭제">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        setDeletingPreset(preset);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* 페이지네이션 */}
            {total > 0 && (
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage="페이지당 행:"
                />
            )}

            {/* 삭제 확인 다이얼로그 */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>설정 삭제</DialogTitle>
                <DialogContent>
                    <Typography>
                        "{deletingPreset?.client} - {deletingPreset?.server_name}" 설정을 삭제하시겠습니까?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                        이 작업은 되돌릴 수 없습니다.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        삭제
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 암호화 옵션 다이얼로그 */}
            <Dialog open={encryptDialogOpen} onClose={() => setEncryptDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <EnhancedEncryptionIcon sx={{fontSize: 20, mr: 1, verticalAlign: 'text-bottom'}}/>
                    ENC 암호화 옵션
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        암호화할 필드를 선택하세요.
                    </Typography>
                    <Box sx={{maxHeight: 240, overflowY: 'auto', mb: 2}}>
                        {encryptDialogFieldList.map(({key, label}) => (
                            <FormControlLabel
                                key={key}
                                sx={{display: 'block'}}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={!!encryptDialogFields[key]}
                                        onChange={(e) => setEncryptDialogFields(prev => ({
                                            ...prev,
                                            [key]: e.target.checked,
                                        }))}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {label} <Typography component="span" variant="caption" color="text.secondary">({key})</Typography>
                                    </Typography>
                                }
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEncryptDialogOpen(false)} disabled={encrypting}>취소</Button>
                    <Button
                        onClick={handleEncryptDialogConfirm}
                        variant="contained"
                        disabled={encrypting}
                    >
                        {encrypting ? '암호화 중...' : '확인'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

// 프로퍼티 파일 내용 생성 헬퍼
function generatePropertiesContent(preset, schema, encryptedValues = {}) {
    if (!schema) return '';

    const values = preset.configData || {};
    const lines = [
        '############################################################',
        '#              config-override.properties                  #',
        `#         ${preset.client} - ${preset.server_name}`,
        '#         Generated by Into Build Config Generator         #',
        '############################################################',
        '',
    ];

    schema.categories.forEach(cat => {
        const visibleProps = cat.properties.filter(prop => {
            if (prop.virtual) return false;
            if (prop.alwaysInclude) return true;
            const value = values[prop.key];
            if (value === undefined || value === null) return false;
            if (value === prop.default) return false;
            if (value === '' && prop.default === '') return false;
            return true;
        });

        if (visibleProps.length > 0) {
            lines.push(`# ${cat.label}`);
            visibleProps.forEach(prop => {
                let value = encryptedValues[prop.key] || (values[prop.key] ?? prop.default);
                if (typeof value === 'boolean') {
                    value = value ? 'true' : 'false';
                }
                lines.push(`${prop.key}=${value}`);
            });
            lines.push('');
        }
    });

    return lines.join('\n');
}
